import { z } from 'zod';
import { normaliseSectionName } from '../utils/ingestName.util';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [amended OTHERS-EXPAND] THE EXCLUDE FILE — `pnpm expand:others -- --exclude <file.json>`.
 *
 *  The expansion creates a section for every `(parent, normalised meta.section)` the officers in
 *  an `Others` unit carry, and the database decides what that is. Some of those values are not
 *  sections at all — `NOT JOINED`, `SINCE CHANGED/INSERTED`, `ATTACHED TO AC/DC` — and a human has
 *  to be able to say so. This file is how: each entry leaves that group's officers in `Others`,
 *  and the report prints the entry's `reason` beside them.
 *
 *  ⚠️ IT CAN ONLY SUBTRACT. There is no merge and no rename — a section's NAME still comes only
 *  from what officers carry. A human who wants `MT` filed with `MT SEC` corrects the officer's
 *  `meta.section`, where the fact lives, rather than teaching this file a second vocabulary.
 *
 *  ⚠️ STRICT, NOT STRIPPED — `establishment.schema.ts`'s argument, verbatim: a hand-edited file
 *  has no privilege to escalate and no reader for a 422, and a stripped key (`reasn`, `sectoin`)
 *  is an exclusion that silently does nothing while the report claims the file was read.
 *
 *  ⚠️ `parent` IS A `short_code`, COMPARED EXACTLY, AND SURROUNDING WHITESPACE IS REFUSED rather
 *  than trimmed — `1ST BN` carries a space the code genuinely has, and `'SB '` is a typo the
 *  planner would otherwise report as an entry that matched nothing.
 *
 *  ⚠️ `section` IS COMPARED AFTER `normaliseSectionName`, the same reduction the expansion groups
 *  on — so `CYBER PS` and `CYBER` name one group, and TWO entries naming one group are refused
 *  here, at the entry that repeats, rather than silently applied once.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

/** Bumped only in a commit that also changes the expansion; `z.literal` refuses a file written for another. */
export const OTHERS_EXPANSION_EXCLUDE_FILE_VERSION = 1;

/** A generous bound on a hand-written file. 1,049 groups exist today; an exclusion per group is already absurd. */
export const OTHERS_EXPANSION_MAX_EXCLUSIONS = 5_000;

const untrimmedRefusal = 'must not start or end with whitespace — the value is compared exactly';

export const othersExpansionExclusionSchema = z.strictObject({
  parent: z
    .string()
    .min(1, 'parent is the short code of the Others unit’s parent, e.g. "SB"')
    .max(24)
    .refine((value) => value === value.trim(), untrimmedRefusal),
  section: z
    .string()
    .max(200)
    .refine((value) => value.trim().length > 0, 'section must not be blank'),
  reason: z
    .string()
    .max(400)
    .refine((value) => value.trim().length > 0, 'every exclusion says why — the report prints it'),
});

export const othersExpansionExcludeFileSchema = z
  .strictObject({
    version: z.literal(OTHERS_EXPANSION_EXCLUDE_FILE_VERSION),
    exclude: z.array(othersExpansionExclusionSchema).max(OTHERS_EXPANSION_MAX_EXCLUSIONS),
  })
  .superRefine((file, context) => {
    const seen = new Map<string, number>();
    file.exclude.forEach((entry, index) => {
      const key = `${entry.parent}\u0000${normaliseSectionName(entry.section)}`;
      const first = seen.get(key);
      if (first === undefined) {
        seen.set(key, index);
        return;
      }
      context.addIssue({
        code: 'custom',
        path: ['exclude', index, 'section'],
        message:
          `repeats exclude[${String(first)}]: both name the group "${normaliseSectionName(entry.section)}" ` +
          `under ${entry.parent}. Keep one.`,
      });
    });
  });

export type OthersExpansionExclusion = z.infer<typeof othersExpansionExclusionSchema>;
export type OthersExpansionExcludeFile = z.infer<typeof othersExpansionExcludeFileSchema>;
